import { useLingui } from "@lingui/react/macro";
import { LabelSettingsCard } from "@/components/labels";
import { ContainerFlagSettingsCard } from "@/components/container-flags";
import { AllergenSettingsCard } from "./AllergenSettingsCard";
import { BrandingSettingsCard } from "./BrandingSettingsCard";
import { CategorySettingsCard } from "./CategorySettingsCard";
import { ListSettingsCard } from "./ListSettingsCard";
import { StationSettingsCard } from "./StationSettingsCard";
import { UnitSettingsCard } from "./UnitSettingsCard";

interface ConfigurationPanelProps {
  /** False renders everything read-only — a cook may read the lists, not rewrite them. */
  canManage: boolean;
}

/**
 * Settings → Configuration (T-1006).
 *
 * "Probably actually like a configuration tab that lets you set up your storage
 * containers, like all that stuff under a config tab" (transcript 53:50). Every
 * list here used to be a constant compiled into this app, so a kitchen that
 * works in bus tubs and never touches a squeeze bottle saw whatever we had
 * guessed at.
 *
 * Everything is tenant-scoped, and everything is additive at the edges: removing
 * an entry never rewrites a recipe already using it, because each picker keeps
 * offering the value it holds.
 */
export function ConfigurationPanel({ canManage }: ConfigurationPanelProps) {
  const { t } = useLingui();

  return (
    <div className="space-y-6">
      {/* Two colours and a logo, not a theme — applied where the food is
          presented (T-1301). */}
      <BrandingSettingsCard canManage={canManage} />

      <CategorySettingsCard canManage={canManage} />

      <ListSettingsCard
        kind="containerSubTypes"
        canManage={canManage}
        title={t`Container types`}
        description={t`The shapes this kitchen stores things in. Offered as the sub-type when a container is added to a recipe.`}
        placeholder={t`e.g. Cambro`}
      />

      <ListSettingsCard
        kind="toolTypes"
        canManage={canManage}
        title={t`Tool types`}
        description={t`The kinds of equipment a recipe can call for.`}
        placeholder={t`e.g. Immersion blender`}
      />

      <ListSettingsCard
        kind="storageMethods"
        canManage={canManage}
        title={t`Storage methods`}
        description={t`How a prepped item is held. Chosen on a shelf life stage, and printed on the label.`}
        placeholder={t`e.g. Blast chilled`}
      />

      <AllergenSettingsCard canManage={canManage} />

      <UnitSettingsCard canManage={canManage} />

      {/* The one card here that belongs to a location rather than the
          organization, so it reads and gates itself (YC-36). */}
      <StationSettingsCard />

      {/* Labels are a tenant setting, not a per-recipe one (T-1001). Everyone
          who may read a recipe may see what its label says; changing it is an
          admin decision. */}
      <LabelSettingsCard canManage={canManage} />

      {/* What a coloured lid means is the kitchen's own convention, so the app
          records it rather than inventing one (T-1005). */}
      <ContainerFlagSettingsCard canManage={canManage} />
    </div>
  );
}
